import { ScaleElement } from '@scale/base-element';
import { CartAPI } from '@scale/cart-api';
import { rollAmount, shownText } from '@scale/cart-motion';
import { formatMoney } from '@scale/currency';
import { shake } from '@scale/utilities';

const moves = () => matchMedia('(prefers-reduced-motion: no-preference)').matches;
const timing = (element) => {
  const style = getComputedStyle(element);
  return { duration: parseFloat(style.getPropertyValue('--duration-normal')), easing: style.getPropertyValue('--ease-out') };
};

class ScaleCartItem extends ScaleElement {
  #confirmed = null;

  get #key() {
    return this.dataset.key;
  }

  get #quantity() {
    return Number(this.dataset.quantity) || 0;
  }

  get #max() {
    return Number(this.dataset.max) || Infinity;
  }

  get #min() {
    return Number(this.dataset.min) || 1;
  }

  get #step() {
    return Number(this.dataset.step) || 1;
  }

  updatedCallback() {
    this.invalidateRefs();
  }

  setup() {
    const input = this.ref('quantity-input');
    if (!input) return;

    const submit = () => {
      const value = parseInt(input.value, 10);
      if (Number.isNaN(value) || value < 0) return;
      if (value === 0) {
        this.remove();
      } else if (value !== this.#quantity) {
        this.#changeQuantity(value);
      }
    };

    input.addEventListener('change', submit, { signal: this.signal });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        input.blur();
        submit();
      }
    }, { signal: this.signal });
  }


  increment() {
    this.#changeQuantity(this.#quantity + this.#step);
  }

  decrement() {
    const newQty = this.#quantity - this.#step;
    if (newQty < this.#min) {
      this.remove();
      return;
    }
    this.#changeQuantity(newQty);
  }

  remove() {
    this.#changeQuantity(0);
  }

  async #changeQuantity(wanted) {
    const step = this.#step;
    const rounded = Math.round(wanted / step) * step;
    const ceiling = Math.floor(this.#max / step) * step;
    const quantity = wanted && Math.max(Math.min(rounded, ceiling), this.#min);
    const limit = this.ref('limit');
    if (limit) limit.hidden = rounded <= ceiling;
    if (rounded > ceiling) this.#refuse(limit);
    if (quantity === this.#quantity) {
      this.ref('quantity-input').value = quantity;
      return;
    }

    const cart = this.closest('scale-cart-drawer, scale-cart-page');
    const nextKey = quantity === 0 && this.contains(document.activeElement) ? this.#neighbourKey(cart) : null;
    const exit = quantity === 0 ? this.#leave() : null;
    if (!exit) this.#estimate(cart, quantity);

    try {
      const sectionId = this.dataset.sectionId;
      await CartAPI.change(this.#key, quantity, {
        sections: sectionId ? [sectionId] : [],
        target: this,
        context: this.closest('dialog, scale-cart-drawer') ? 'dialog' : 'cart',
        ready: exit?.finished,
      });
      this.#confirmed = null;
      if (nextKey !== null) {
        const neighbour = nextKey && cart.querySelector(`scale-cart-item[data-key="${CSS.escape(nextKey)}"] button`);
        (neighbour || cart.querySelector('button'))?.focus();
      }
      this.announce(`${Scale.translations.quantity}: ${quantity}`, 'polite');
    } catch (err) {
      if (exit) {
        await exit.finished;
        this.#comeBack(exit);
      } else if (err?.name !== 'AbortError') {
        this.#restore();
      }
    }
  }

  #neighbourKey(cart) {
    const items = [...cart.querySelectorAll('scale-cart-item')];
    const index = items.indexOf(this);
    return (items[index + 1] ?? items[index - 1])?.dataset.key ?? '';
  }

  #leave() {
    const style = getComputedStyle(this);
    const duration = parseFloat(style.getPropertyValue('--duration-normal'));
    const moving = moves();
    const size = {
      blockSize: `${this.offsetHeight}px`,
      paddingBlock: `${style.paddingBlockStart} ${style.paddingBlockEnd}`,
      borderBlockEndWidth: style.borderBlockEndWidth,
    };
    const shift = `${style.direction === 'rtl' ? -24 : 24}px 0`;

    this.inert = true;
    this.classList.add('cart-item--leaving');
    const slide = this.animate(
      [{ opacity: 1, translate: '0 0' }, { opacity: 0, translate: moving ? shift : '0 0' }],
      { duration, easing: style.getPropertyValue('--ease-in'), fill: 'forwards' },
    );
    const fold = this.animate(
      [size, { blockSize: '0px', paddingBlock: '0px 0px', borderBlockEndWidth: '0px' }],
      { duration: moving ? duration : 0, delay: moving ? duration * 0.6 : duration, easing: style.getPropertyValue('--ease-out'), fill: 'forwards' },
    );
    return { slide, fold, size, shift, moving, finished: Promise.all([slide.finished, fold.finished]) };
  }

  #comeBack({ slide, fold, size, shift, moving }) {
    slide.cancel();
    fold.cancel();
    this.inert = false;
    this.classList.remove('cart-item--leaving');
    if (!moving) return;
    const style = getComputedStyle(this);
    const duration = parseFloat(style.getPropertyValue('--duration-normal'));
    this.animate(
      [
        { blockSize: '0px', paddingBlock: '0px 0px', borderBlockEndWidth: '0px', opacity: 0, translate: shift },
        { ...size, opacity: 0, translate: shift, offset: 0.5 },
        { ...size, opacity: 1, translate: '0 0' },
      ],
      { duration: duration * 2, easing: style.getPropertyValue('--ease-out') },
    );
  }

  #estimate(cart, quantity) {
    const current = this.#quantity;
    const line = this.querySelector('[data-cart-roll="final"]');
    const scaled = (element) => Math.round((Number(element.dataset.cents) * quantity) / current) - Number(element.dataset.cents);
    const lineChange = scaled(line);
    const was = this.querySelector('[data-cart-roll="was"]');
    const wasChange = scaled(was);
    const amounts = [
      [line, lineChange],
      [was, wasChange],
      ...[...this.querySelectorAll('[data-cart-roll^="discount-"]')].map((element) => [element, 0]),
      ...[...cart.querySelectorAll('[data-cart-roll="total"]')].map((element) => [element, lineChange]),
      ...[...cart.querySelectorAll('[data-cart-roll="savings"]')].map((element) => [element, wasChange - lineChange]),
    ];
    const counts = [...cart.querySelectorAll('[data-cart-roll="count"]')];
    this.#confirmed ??= {
      quantity: current,
      amounts: [...amounts.map(([element]) => element), ...counts].map((element) => [element, shownText(element), element.dataset.cents]),
    };

    this.dataset.quantity = quantity;
    const input = this.ref('quantity-input');
    input.value = quantity;
    if (moves()) input.animate([{ translate: `0 ${quantity > current ? 35 : -35}%`, opacity: 0 }, { translate: '0 0', opacity: 1 }], timing(this));
    for (const [element, change] of amounts) {
      element.classList.add('is-pending');
      if (!change) continue;
      element.dataset.cents = Number(element.dataset.cents) + change;
      rollAmount(element, formatMoney(Number(element.dataset.cents)));
    }
    for (const count of counts) rollAmount(count, String(Number(shownText(count)) + quantity - current));
  }

  #restore() {
    const { quantity, amounts } = this.#confirmed;
    this.#confirmed = null;
    this.dataset.quantity = quantity;
    this.ref('quantity-input').value = quantity;
    for (const [element, text, cents] of amounts) {
      element.classList.remove('is-pending');
      if (cents !== undefined) element.dataset.cents = cents;
      rollAmount(element, text);
    }
  }

  #refuse(limit) {
    this.announce(limit.textContent, 'polite');
    shake(this.ref('quantity'));
  }
}

if (!customElements.get('scale-cart-item')) {
  customElements.define('scale-cart-item', ScaleCartItem);
}
